var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { inject, injectable } from '@servicetitan/react-ioc';
import { arrayToMap } from '../../utils/collection-helpers';
import { CONTENTFUL_API_TOKEN } from '../api/contentful.api';
/**
 * This store used for loading and accessing "serviceTitanPage" contentful object by given codeId. How to use:
 * 1. Provide this store
 * 2. Call hook useContentfulPage(codeId) to run lazy initialization
 * 3. Page data will be available when initialization is done
 */
let PageContentfulStore = class PageContentfulStore {
    constructor(contentfulApi) {
        Object.defineProperty(this, "contentfulApi", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: contentfulApi
        });
        Object.defineProperty(this, "cancelToken", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "isInitializing", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isInitialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "pageContentfulData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "tooltips", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "initialize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (options) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                if (this.isInitialized || this.isInitializing) {
                    return;
                }
                this.isInitializing = true;
                try {
                    this.cancelToken = axios.CancelToken.source();
                    const result = yield this.contentfulApi.getContentfulData({
                        entryType: 'serviceTitanPage',
                        codeId: options.codeId,
                        isPreview: (_a = options.isPreview) !== null && _a !== void 0 ? _a : false,
                        include: (_b = options.include) !== null && _b !== void 0 ? _b : 10,
                    }, this.cancelToken.token);
                    runInAction(() => {
                        var _a;
                        this.cancelToken = undefined;
                        this.pageContentfulData = result;
                        this.tooltips = arrayToMap((_a = result === null || result === void 0 ? void 0 : result.tooltips) !== null && _a !== void 0 ? _a : [], x => x.objectId);
                        this.isInitialized = true;
                    });
                }
                finally {
                    runInAction(() => {
                        this.isInitializing = false;
                    });
                }
            })
        });
        Object.defineProperty(this, "lazyInitialize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (options) => __awaiter(this, void 0, void 0, function* () {
                if (!this.isInitialized && !this.isInitializing) {
                    return this.initialize(options);
                }
            })
        });
        Object.defineProperty(this, "destroy", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.cancelToken) {
                    this.cancelToken.cancel();
                    this.cancelToken = undefined;
                }
            }
        });
        /**
         * Get tooltip for given object id.
         * @param objectId Contentful tooltip objectId field value. This tooltip should be added to the "tooltips" section
         * in the loaded "serviceTitanPage" entry.
         * @param disabled Get "disabled" version of tooltip with object id "objectId-disabled"
         */
        Object.defineProperty(this, "getTooltip", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (objectId, disabled = false) => {
                var _a;
                let tooltip = disabled ? this.tooltips.get(`${objectId}-disabled`) : undefined;
                tooltip = tooltip !== null && tooltip !== void 0 ? tooltip : this.tooltips.get(objectId);
                const content = tooltip === null || tooltip === void 0 ? void 0 : tooltip.content;
                return (_a = (content === '%none%' ? undefined : content)) !== null && _a !== void 0 ? _a : '';
            }
        });
        makeObservable(this);
    }
    /**
     * Get "serviceTitanPage" entry
     */
    getPage() {
        return this.pageContentfulData;
    }
};
__decorate([
    observable,
    __metadata("design:type", Object)
], PageContentfulStore.prototype, "isInitializing", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PageContentfulStore.prototype, "isInitialized", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PageContentfulStore.prototype, "pageContentfulData", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], PageContentfulStore.prototype, "tooltips", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], PageContentfulStore.prototype, "initialize", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], PageContentfulStore.prototype, "lazyInitialize", void 0);
PageContentfulStore = __decorate([
    injectable(),
    __param(0, inject(CONTENTFUL_API_TOKEN)),
    __metadata("design:paramtypes", [Object])
], PageContentfulStore);
export { PageContentfulStore };
//# sourceMappingURL=page-contentful.store.js.map